import { getJson } from '../services/api.js';
import { getLanguage } from '../services/i18n.js';
import { filePlatforms } from '../services/filePlatforms.js';
import { element, showMessage } from '../components/publicUi.js';

const providers = { YANDEX_DISK: 'Яндекс Диск', GOOGLE_DRIVE: 'Google Диск', DROPBOX: 'Dropbox', ONEDRIVE: 'OneDrive', OTHER: 'Внешний ресурс' };
const validId = value => /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(value);
const validSlug = value => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

export class FilesPage {
  constructor(onNavigate, initialCategory = 'all', request = getJson) {
    this.onNavigate = onNavigate; this.request = request; this.page = 1; this.sequence = 0;
    const [location, query = ''] = String(initialCategory || 'all').split('?');
    const params = new URLSearchParams(query);
    this.sectionSlug = location.startsWith('section/') && validSlug(location.slice(8)) ? location.slice(8) : null;
    this.platform = Object.hasOwn(filePlatforms, location) ? location
      : Object.hasOwn(filePlatforms, params.get('platform')) ? params.get('platform') : 'all';
    this.searchQuery = (params.get('q') || '').slice(0, 200);
  }
  render() {
    const root = element('section', 'container page-wrapper');
    const header = element('div', 'page-header'); this.breadcrumb = element('nav', 'page-breadcrumb file-breadcrumb');
    this.breadcrumb.setAttribute('aria-label', 'Путь по разделам файлов');
    header.append(this.breadcrumb, element('h1', 'page-title', '>_ ./files.bin'),
      element('p', 'page-desc', 'Каталог программ и материалов. Выберите раздел и платформу; ссылки откроют внешний файлообменник.'));
    const layout = element('div', 'file-catalog-layout');
    this.menu = element('details', 'file-section-menu gallery-section-menu');
    this.menu.open = !(typeof window !== 'undefined' && window.matchMedia?.('(max-width: 760px)').matches);
    this.menu.append(element('summary', 'file-menu-title', 'Разделы файлов'));
    this.sectionTree = element('nav'); this.sectionTree.setAttribute('aria-label', 'Разделы файлов'); this.menu.append(this.sectionTree);
    const main = element('div', 'file-catalog-main');
    this.filters = element('div', 'category-buttons file-platform-filters'); this.renderFilters();
    this.search = element('input', 'auth-input file-search'); this.search.type = 'search'; this.search.maxLength = 200;
    this.search.value = this.searchQuery; this.search.placeholder = 'Поиск по названию и описанию'; this.search.setAttribute('aria-label', 'Поиск файлов');
    this.search.addEventListener('input', () => { clearTimeout(this.debounce); this.controller?.abort(); this.sequence++;
      this.debounce = setTimeout(() => { this.searchQuery = this.search.value; this.page = 1; this.updateHash(); void this.load(); }, 250); });
    this.content = element('div', 'file-card-grid'); this.content.setAttribute('role', 'region'); this.content.setAttribute('aria-label', 'Каталог файлов');
    this.pager = element('nav', 'blog-pagination'); this.pager.setAttribute('aria-label', 'Страницы файлов');
    main.append(this.filters, this.search, this.content, this.pager); layout.append(this.menu, main); root.append(header, layout);
    void this.initialize(); return root;
  }
  async initialize() {
    this.sectionController?.abort(); this.sectionController = new AbortController(); const { signal } = this.sectionController;
    showMessage(this.content, 'Загрузка разделов…');
    try {
      const result = await this.request(`/file-sections?lang=${getLanguage()}`, { signal }); if (signal.aborted || this.disposed) return;
      this.sections = Array.isArray(result.data) ? result.data : [];
      this.renderSections(); this.renderBreadcrumb(); await this.load();
    } catch (error) { if (!signal.aborted && !this.disposed) showMessage(this.content, error.message, () => this.initialize()); }
  }
  routeSuffix() {
    const params = new URLSearchParams();
    if (this.platform !== 'all') params.set('platform', this.platform);
    if (this.searchQuery) params.set('q', this.searchQuery);
    const path = this.sectionSlug ? `section/${this.sectionSlug}` : 'all';
    return `${path}${params.size ? `?${params}` : ''}`;
  }
  updateHash() {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const suffix = this.routeSuffix();
    window.history.replaceState(null, '', suffix === 'all' ? '#files' : `#files/${suffix}`);
  }
  selectSection(slug) {
    if (slug === this.sectionSlug || this.disposed) return;
    clearTimeout(this.debounce); this.sectionSlug = slug; this.page = 1;
    if (this.onNavigate) this.onNavigate('files', this.routeSuffix());
    else { this.updateHash(); this.renderSections(); this.renderBreadcrumb(); void this.load(); }
  }
  renderSections() {
    this.sectionTree.replaceChildren();
    const root = element('ul', 'file-section-tree');
    const all = element('button', `file-section-link${this.sectionSlug ? '' : ' active'}`, 'Все файлы');
    all.type = 'button'; all.setAttribute('aria-current', this.sectionSlug ? 'false' : 'page');
    all.addEventListener('click', () => this.selectSection(null));
    const rootItem = element('li'); rootItem.append(all); root.append(rootItem);
    const byParent = new Map();
    for (const section of this.sections || []) {
      const key = section.parentId || null; if (!byParent.has(key)) byParent.set(key, []); byParent.get(key).push(section);
    }
    const visited = new Set();
    const appendChildren = (parentId, target) => {
      for (const section of byParent.get(parentId) || []) {
        if (visited.has(section.id)) continue; visited.add(section.id);
        const item = element('li'), button = element('button', `file-section-link${section.slug === this.sectionSlug ? ' active' : ''}`, section.name);
        button.type = 'button'; button.setAttribute('aria-current', section.slug === this.sectionSlug ? 'page' : 'false');
        button.addEventListener('click', () => this.selectSection(section.slug)); item.append(button);
        if (byParent.has(section.id)) { const children = element('ul', 'file-section-tree'); appendChildren(section.id, children); item.append(children); }
        target.append(item);
      }
    };
    appendChildren(null, root); this.sectionTree.append(root);
  }
  renderBreadcrumb() {
    this.breadcrumb.replaceChildren();
    const home = element('button', 'file-breadcrumb-link', 'Файлы'); home.type = 'button';
    home.addEventListener('click', () => this.selectSection(null)); this.breadcrumb.append(home);
    if (!this.sectionSlug) return;
    const byId = new Map((this.sections || []).map(section => [section.id, section]));
    const path = []; const seen = new Set(); let current = (this.sections || []).find(section => section.slug === this.sectionSlug);
    while (current && !seen.has(current.id)) { seen.add(current.id); path.unshift(current); current = current.parentId ? byId.get(current.parentId) : null; }
    if (!path.length) { this.breadcrumb.append(element('span', '', ' / Раздел недоступен')); return; }
    for (const section of path) {
      this.breadcrumb.append(element('span', '', ' / '));
      const link = element('button', 'file-breadcrumb-link', section.name); link.type = 'button';
      link.addEventListener('click', () => this.selectSection(section.slug)); this.breadcrumb.append(link);
    }
  }
  renderFilters() {
    this.filters.replaceChildren();
    for (const [value, label] of [['all', 'Все платформы'], ...Object.entries(filePlatforms)]) {
      const button = element('button', `filter-btn${value === this.platform ? ' active' : ''}`, label); button.type = 'button';
      button.setAttribute('aria-pressed', String(value === this.platform));
      button.addEventListener('click', () => { clearTimeout(this.debounce); this.platform = value; this.page = 1;
        this.renderFilters(); this.updateHash(); void this.load(); }); this.filters.append(button);
    }
  }
  async load() {
    if (this.disposed) return;
    const sequence = ++this.sequence; this.controller?.abort(); this.controller = new AbortController(); const { signal } = this.controller;
    showMessage(this.content, 'Загрузка файлов…'); this.pager.replaceChildren();
    const query = new URLSearchParams({ page: String(this.page), limit: '9' });
    query.set('lang', getLanguage());
    if (this.platform !== 'all') query.set('platform', this.platform);
    if (this.sectionSlug) query.set('section', this.sectionSlug);
    if (this.searchQuery) query.set('q', this.searchQuery);
    try {
      const result = await this.request(`/files?${query}`, { signal }); if (signal.aborted || this.disposed || sequence !== this.sequence) return;
      this.content.replaceChildren();
      if (!result.data.length) showMessage(this.content, this.searchQuery || this.sectionSlug || this.platform !== 'all'
        ? 'По выбранным условиям файлы не найдены.' : 'Опубликованных файлов пока нет.');
      for (const file of result.data) this.content.append(this.renderCard(file));
      const { page, total, totalPages } = result.meta, prev = element('button', 'filter-btn', '← Назад'), next = element('button', 'filter-btn', 'Вперёд →');
      prev.type = next.type = 'button'; prev.disabled = page <= 1; next.disabled = page >= totalPages;
      prev.addEventListener('click', () => { this.page--; void this.load(); }); next.addEventListener('click', () => { this.page++; void this.load(); });
      this.pager.append(prev, element('span', '', `Страница ${page} из ${Math.max(1, totalPages)} · Всего: ${total}`), next);
    } catch (error) { if (!signal.aborted && !this.disposed && sequence === this.sequence) showMessage(this.content, error.message, () => this.load()); }
  }
  renderCard(file) {
    const card = element('article', 'file-product-card');
    const cover = element('div', 'file-cover-placeholder'); cover.setAttribute('role', 'img');
    cover.setAttribute('aria-label', `Заглушка раздела ${file.sections?.[0]?.name || 'Файлы'}`);
    cover.append(element('span', 'file-cover-symbol', '▣'), element('span', '', file.sections?.[0]?.name || 'Файлы'));
    let illustration = cover;
    if (typeof file.coverUrl === 'string' && /^\/api\/v1\/gallery\/[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}\/media\/preview$/i.test(file.coverUrl)) {
      const image = element('img', 'file-cover-image'); image.src = file.coverUrl;
      image.alt = file.coverAlt || `Обложка: ${file.title}`; image.loading = 'lazy';
      image.addEventListener('error', () => { if (image.isConnected) image.replaceWith(cover); }); illustration = image;
    }
    const body = element('div', 'file-product-body'), title = element('h2', 'file-name-highlight', file.title);
    const summary = element('p', 'profile-text', file.summary || file.description || 'Описание пока не добавлено.');
    const platforms = element('div', 'file-platform-tags');
    for (const platform of file.platforms || []) platforms.append(element('span', 'skill-tag', filePlatforms[platform] || platform));
    const action = element('button', 'read-btn', 'Подробнее →'); action.type = 'button'; action.setAttribute('aria-label', `Подробнее: ${file.title}`);
    action.addEventListener('click', () => this.openModal(file, action));
    body.append(title, summary, platforms, element('p', 'file-version-count', `Версий: ${file.variants?.length || 0}`), action);
    card.append(illustration, body); return card;
  }
  openModal(file, trigger) {
    this.closeModal?.();
    const backdrop = element('div', 'modal-backdrop'), modal = element('section', 'modal-window file-detail-dialog');
    modal.setAttribute('role', 'dialog'); modal.setAttribute('aria-modal', 'true'); modal.setAttribute('aria-label', `Описание: ${file.title}`);
    const header = element('div', 'terminal-header'), close = element('button', 'modal-close-red', '[ ESC / CLOSE ✕ ]');
    close.type = 'button'; close.setAttribute('aria-label', 'Закрыть описание файла'); header.append(element('div', 'terminal-title', file.title), close);
    const body = element('div', 'modal-body');
    body.append(element('h2', 'page-title', file.title), element('p', 'profile-text', file.description || file.summary || 'Описание пока не добавлено.'));
    const tags = element('div', 'file-platform-tags');
    for (const platform of file.platforms || []) tags.append(element('span', 'skill-tag', filePlatforms[platform] || platform));
    body.append(tags, element('h3', 'profile-group', 'Версии и внешние ссылки'));
    for (const variant of file.variants || []) {
      const row = element('div', 'file-variant-row');
      row.append(element('strong', '', variant.label), element('span', '', `${filePlatforms[variant.platform] || variant.platform} · ${providers[variant.provider] || providers.OTHER} · ${variant.host || ''}`));
      if (validId(file.id) && validId(variant.id)) {
        const link = element('a', 'download-action-btn', 'Открыть внешний файл ↗');
        link.href = `/api/v1/files/${file.id}/download?variant=${variant.id}`; link.target = '_blank'; link.rel = 'noopener noreferrer';
        row.append(link);
      } else row.append(element('span', '', 'Ссылка недоступна'));
      body.append(row);
    }
    modal.append(header, body); backdrop.append(modal); document.body.append(backdrop); close.focus();
    const keydown = event => {
      if (event.key === 'Escape') this.closeModal?.();
      if (event.key === 'Tab') {
        const focusable = [...modal.querySelectorAll('button:not(:disabled),a[href]')], first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    this.closeModal = () => { backdrop.remove(); document.removeEventListener('keydown', keydown); this.closeModal = null; if (trigger?.isConnected) trigger.focus(); };
    close.addEventListener('click', () => this.closeModal?.()); backdrop.addEventListener('click', event => { if (event.target === backdrop) this.closeModal?.(); });
    document.addEventListener('keydown', keydown);
  }
  dispose() { this.disposed = true; clearTimeout(this.debounce); this.sectionController?.abort(); this.controller?.abort(); this.closeModal?.(); this.content?.replaceChildren(); }
}
